/**
 * ReasonSelector Component
 *
 * Grid of selectable reason cards for decline feedback.
 * Shows predefined reasons with icons and descriptions.
 */
import {
    AccessTime,
    BuildOutlined,
    Business,
    CalendarToday,
    Clear,
    LocationOnOutlined,
    WorkOffOutlined
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DeclineFeedbackFormData, DeclineReason, ReasonInputFormData } from '../../types';
import ReasonCard from './ReasonCard';

interface ReasonSelectorProps {
    onSelectReason: () => void;
}

export interface ReasonConfig {
    id: DeclineReason;
    icon: React.ReactNode;
}

const REASON_CONFIGS: ReasonConfig[] = [
    { id: 'availability', icon: <CalendarToday /> },
    { id: 'schedule', icon: <AccessTime /> },
    { id: 'location', icon: <LocationOnOutlined /> },
    { id: 'company', icon: <Business /> },
    { id: 'role', icon: <WorkOffOutlined /> },
    { id: 'skills', icon: <BuildOutlined /> },
    { id: 'other', icon: <Clear /> }
];

const REASON_TO_INPUT_TYPE_MAP: Record<DeclineReason, ReasonInputFormData['type'] | null> = {
    availability: 'availability',
    location: 'location',
    company: null,
    role: 'role',
    skills: 'skills',
    schedule: null,
    other: null
};

export default function ReasonSelector({ onSelectReason }: ReasonSelectorProps) {
    const { t } = useTranslation('feedback');
    const { setValue } = useFormContext<DeclineFeedbackFormData>();

    const handleSelectReason = (reason: DeclineReason) => {
        setValue('primaryReason', reason, { shouldDirty: true });

        const inputType = REASON_TO_INPUT_TYPE_MAP[reason];

        if (inputType) {
            setValue('reasonInput.type', inputType, { shouldDirty: true });
        } else {
            setValue('reasonInput', null, { shouldDirty: true });
        }

        onSelectReason();
    };

    return (
        <Grid container
            spacing={ 2 }>
            { REASON_CONFIGS.map((config) => (
                <Grid key={ config.id }
                    size={{ xs: 12, sm: config.id === 'other' ? 12 : 6 }}
                >
                    <ReasonCard
                        key={ config.id }
                        reason={ config }
                        onSelect={ handleSelectReason }
                        title={ t(`declineDialog.selectReason.reasons.${ config.id }.title`) }
                        subtitle={ t(`declineDialog.selectReason.reasons.${ config.id }.subtitle`) }
                    />
                </Grid>
            )) }
        </Grid>
    );
}
